import { useEffect, useState } from 'react';

import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { supplierApi } from 'api';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, UseMutateFunction, UseQueryOptions } from 'react-query';
import { Config, ConfigTypeOptions } from 'v2types';

type UseConfigsQueryType = {
  configs?: Config[];
  isConfigsLoading: boolean;
  refetchConfigs: () => void;
  auditTypeOptions: ConfigTypeOptions[];
  supplierTypeOptions: ConfigTypeOptions[];
  riskTypeOptions: ConfigTypeOptions[];
  documentTypeOptions: ConfigTypeOptions[];
};

export const useConfigsQuery = (queryOptions?: Pick<UseQueryOptions, 'enabled'>): UseConfigsQueryType => {
  const { companyId } = useCurrentUser();
  const [auditTypeOptions, setAuditTypeOptions] = useState<ConfigTypeOptions[]>([{ label: '', value: '' }]);
  const [supplierTypeOptions, setSupplierTypeOptions] = useState<ConfigTypeOptions[]>([{ label: '', value: '' }]);
  const [riskTypeOptions, setRiskTypeOptions] = useState<ConfigTypeOptions[]>([{ label: '', value: '' }]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<ConfigTypeOptions[]>([{ label: '', value: '' }]);

  const {
    data: configs,
    isLoading: isConfigsLoading,
    refetch: refetchConfigs,
  } = useQuery(`supplier-configs-${companyId}`, () => supplierApi.listConfigs(companyId), queryOptions);

  useEffect(() => {
    const auditTypes = configs?.find((item) => item.type === 'Audit')?.options;
    if (auditTypes?.length) {
      setAuditTypeOptions(
        auditTypes.map(
          (auditType): ConfigTypeOptions => ({
            label: auditType.title || '',
            value: auditType.id || '',
            payload: auditType,
          }),
        ),
      );
    }

    const supplierTypes = configs?.find((item) => item.type === 'Type')?.options;
    if (supplierTypes?.length) {
      setSupplierTypeOptions(
        supplierTypes.map(
          (supplierType): ConfigTypeOptions => ({
            label: supplierType.title || '',
            value: supplierType.id || '',
            payload: supplierType,
          }),
        ),
      );
    }

    const riskTypes = configs?.find((item) => item.type === 'Risk')?.options;
    if (riskTypes?.length) {
      setRiskTypeOptions(
        riskTypes.map(
          (riskType): ConfigTypeOptions => ({
            label: riskType.title || '',
            value: riskType.id || '',
            payload: riskType,
          }),
        ),
      );
    }

    const documentTypes = configs?.find((item) => item.type === 'Document')?.options;
    if (documentTypes?.length) {
      setDocumentTypeOptions(
        documentTypes.map(
          (documentType): ConfigTypeOptions => ({
            label: documentType.title || '',
            value: documentType.id || '',
            payload: documentType,
          }),
        ),
      );
    }
  }, [configs]);

  return {
    configs,
    isConfigsLoading,
    refetchConfigs,
    auditTypeOptions,
    supplierTypeOptions,
    riskTypeOptions,
    documentTypeOptions,
  };
};

type UsePostConfigType = {
  mutateConfigs: UseMutateFunction<Config, unknown, Config, unknown>;
  isConfigsMutateLoading: boolean;
};

export const usePostConfig = (onSuccess: () => void): UsePostConfigType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { mutate: mutateConfigs, isLoading: isConfigsMutateLoading } = useMutation(
    (config: Config) => supplierApi.postConfig(companyId, config),
    {
      onSuccess: async (data) => {
        // Map config type to translation key
        const configTypeMap: Record<string, string> = {
          Type: t('policyConfiguration.tabs.supplierTypes'),
          Risk: t('policyConfiguration.tabs.riskLevels'),
          Document: t('policyConfiguration.tabs.documentTypes'),
          Audit: t('policyConfiguration.tabs.auditTypes'),
        };
        const updatedConfig = configTypeMap[data.type] || t('toastMessages.config.configuration');
        showToast({
          description: t('toastMessages.config.updated', { configType: updatedConfig }),
          status: 'success',
          'data-cy': 'toast-config-updated',
        });
        onSuccess();
      },
      onError: (error: any) => {
        showToast({
          description: error?.response?.data?.message ?? t('toastMessages.config.updateFailed'),
          status: 'error',
        });
      },
    },
  );

  return { mutateConfigs, isConfigsMutateLoading };
};
